/**
 * The machine-first Workers board: every online Worker as a compact card under the computer
 * that runs it, and its terminal only when the owner asks for it.
 *
 * A Worker's surface is mounted on demand (#962): none on first load, and one the first time
 * a card's Terminal action presents it as a modal (and, from there, full screen). Once
 * mounted it stays in one stable, flat container while that Worker is on the roster, so
 * presenting, returning, closing and choosing another Worker only restyle that same element,
 * and a terminal is never remounted, duplicated or reconnected by this board.
 */
import { useState, type ReactElement, type ReactNode } from 'react';
import { CollapsedWorkerCard } from './CollapsedWorkerCard';
import { WorkerTerminalWorkspace } from './WorkerTerminalWorkspace';
import { WorkerStatusIndicator } from './WorkerStatusIndicator';
import { workerLastActivityText, type CockpitWorkerCard } from '../lib/cockpit-view';
import type { FinishedRun } from '../lib/data';
import { workerCardAiStatus, type AiExecutionProfileView } from '../lib/ai-execution-profile';
import type { WorkerOfferingChoice } from '../lib/worker-offering-choice';
import { WorkerOfferingSelect } from './WorkerOfferingSelect';
import { workerWorkloadFacts } from '../lib/worker-workload';
import {
  WORKER_BOARD_STATE_LABEL,
  folderLabel,
  workerAssignment,
  workerBoardState,
  workersByMachine,
  type WorkerBoardState,
} from '../lib/worker-board';

export interface CockpitMachineMeta {
  readonly nodeId: string;
  readonly name: string;
  readonly seats: number | null;
  readonly activeRuns: number | null;
  readonly accepting: boolean | null;
  readonly holdingBack: string | null;
  readonly offerings: readonly string[];
}

export interface CockpitWorkersProps {
  /** The online roster only (#584). The board never widens it. */
  readonly cards: readonly CockpitWorkerCard[];
  readonly machines?: readonly CockpitMachineMeta[];
  readonly results?: readonly FinishedRun[];
  readonly aiProfiles?: ReadonlyMap<string, AiExecutionProfileView>;
  /** #250 each Worker's AI selector state; with onChangeOffering, the card shows the selector. */
  readonly offeringChoices?: ReadonlyMap<string, WorkerOfferingChoice>;
  readonly offeringBusy?: boolean;
  readonly offeringError?: string | null;
  readonly onChangeOffering?: (workerId: string, nodeId: string, offeringKey: string | null) => void;
  /** The Worker whose terminal is presented, or null. A view choice only; it routes nothing. */
  readonly terminalWorkerId: string | null;
  readonly onOpenTerminal: (workerId: string) => void;
  readonly onCloseTerminal: () => void;
  /**
   * Draws one Worker's surface, only once that Worker's terminal has been opened.
   * `active` is true only while its terminal is presented.
   */
  readonly renderSurface: (workerId: string, active: boolean) => ReactNode;
  /** The same clock the cards were built with. Absent means the age line is left off. */
  readonly nowMs?: number;
  readonly empty?: string;
}

const STATE_ORDER: readonly WorkerBoardState[] = ['attention', 'working', 'idle', 'offline', 'unknown'];

function stateSummary(cards: readonly CockpitWorkerCard[]): string {
  const counts = new Map<WorkerBoardState, number>();
  for (const card of cards) {
    const state = workerBoardState(card);
    counts.set(state, (counts.get(state) ?? 0) + 1);
  }
  const unassigned = cards.filter((card) => workerAssignment(card) === 'unassigned').length;
  const parts = STATE_ORDER
    .filter((state) => (counts.get(state) ?? 0) > 0)
    .map((state) => `${counts.get(state)} ${WORKER_BOARD_STATE_LABEL[state].toLowerCase()}`);
  if (unassigned > 0) parts.push(`${unassigned} unassigned`);
  return parts.join(' · ');
}

export function CockpitWorkers({
  cards,
  machines = [],
  results = [],
  aiProfiles = new Map<string, AiExecutionProfileView>(),
  offeringChoices = new Map<string, WorkerOfferingChoice>(),
  offeringBusy = false,
  offeringError = null,
  onChangeOffering,
  terminalWorkerId,
  onOpenTerminal,
  onCloseTerminal,
  renderSurface,
  nowMs,
  empty = 'No Workers yet. A Worker appears here as soon as a computer offers one.',
}: CockpitWorkersProps): ReactElement {
  const machineById = new Map(machines.map((machine) => [machine.nodeId, machine]));
  const groups = workersByMachine(cards, new Map(machines.map((machine) => [machine.nodeId, machine.name])));
  const presented = terminalWorkerId !== null && cards.some((card) => card.workerId === terminalWorkerId)
    ? terminalWorkerId
    : null;
  const presentedLeft = terminalWorkerId !== null && presented === null;

  return (
    <div className="cockpit-workers stack" aria-label="Workers">
      {cards.length === 0 && <p className="muted">{empty}</p>}
      {offeringError !== null && <p role="alert">{offeringError}</p>}

      {presentedLeft && (
        <p className="muted worker-focus-offline" role="status">
          The Worker whose terminal was open is no longer online. Its record is kept; choose another Worker.
        </p>
      )}

      {groups.map((group) => {
        const meta = group.machineId === null ? null : machineById.get(group.machineId) ?? null;
        const count = `${group.cards.length} Worker${group.cards.length === 1 ? '' : 's'}`;
        const seatText = meta?.seats === null || meta?.seats === undefined ? count : `${count} · ${meta.seats} seats`;
        const availability = meta?.accepting === false
          ? `holding: ${meta.holdingBack?.replaceAll('_', ' ') ?? 'capacity'}`
          : meta?.accepting === true ? 'room available' : null;
        const key = group.machineId ?? '__not_attached__';

        return (
          <section className={`cockpit-machine-group${group.unattached ? ' is-unattached' : ''}`} key={key}
            aria-label={`${group.name} Workers`}>
            <header className="cockpit-machine-heading">
              <span className="cockpit-machine-title">
                <span className="cockpit-machine-icon" aria-hidden="true" />
                <h3>{group.name}</h3>
              </span>
              <small className="cockpit-machine-facts">
                {seatText}
                {availability ? ` · ${availability}` : ''}
              </small>
              <small className="cockpit-machine-states">{stateSummary(group.cards)}</small>
              {group.unattached && (
                <small className="cockpit-machine-note">
                  No computer is recorded for these Workers, so they cannot be placed under one.
                </small>
              )}
              {meta && meta.offerings.length > 0 && (
                <small className="cockpit-machine-offerings" title={meta.offerings.join(' · ')}>
                  AI available: {meta.offerings.slice(0, 2).join(' · ')}
                  {meta.offerings.length > 2 ? ` +${meta.offerings.length - 2}` : ''}
                </small>
              )}
            </header>
            <ul className="cockpit-worker-grid" aria-label={`${group.name} Worker cards`}>
              {group.cards.map((card) => (
                <li key={card.workerId} className={card.workerId === presented ? 'is-selected' : undefined}
                  aria-current={card.workerId === presented || undefined}>
                  <CollapsedWorkerCard
                    workerNumber={card.workerNumber}
                    workerName={card.workerName}
                    status={card.status}
                    state={workerBoardState(card)}
                    assignment={workerAssignment(card)}
                    activity={card.activity}
                    project={card.project}
                    runtime={card.runtime}
                    model={card.model}
                    machine={null}
                    folder={folderLabel(card.folder)}
                    folderPath={card.folder}
                    elapsed={card.elapsed}
                    phase={card.phase}
                    department={card.department}
                    workload={workerWorkloadFacts(results, card.workerId)}
                    attention={card.attention}
                    terminalWaiting={card.terminalWaiting === true}
                    aiMismatch={aiProfiles.get(card.workerId)?.mismatch === true}
                    aiStatus={(() => {
                      const profile = aiProfiles.get(card.workerId);
                      return profile === undefined ? null : workerCardAiStatus(profile);
                    })()}
                    aiControl={(() => {
                      const choice = offeringChoices.get(card.workerId);
                      return choice === undefined || onChangeOffering === undefined ? undefined : (
                        <WorkerOfferingSelect choice={choice} busy={offeringBusy} onChange={onChangeOffering}
                          className="worker-card-ai-select row" />
                      );
                    })()}
                    statusIndicator={<><WorkerStatusIndicator status={card.status} />{card.beacon !== undefined && <WorkerActivityBeacon beacon={card.beacon} lastActivityAt={card.lastActivityAt} nowMs={nowMs} />}</>}
                    terminalId={card.workerId === presented ? `worker-${card.workerNumber}-workspace` : undefined}
                    terminalOpen={card.workerId === presented}
                    onOpenTerminal={() => onOpenTerminal(card.workerId)}
                  />
                </li>
              ))}
            </ul>
          </section>
        );
      })}

      {presented !== null && <div className="terminal-modal-backdrop" aria-hidden="true" onClick={onCloseTerminal} />}

      <WorkerFocusStage cards={cards} presented={presented} onCloseTerminal={onCloseTerminal}
        renderSurface={renderSurface} />
    </div>
  );
}

/** #732 beacon, with the #733 age line under it. The line uses the card's recorded time only. */
function WorkerActivityBeacon({ beacon, lastActivityAt, nowMs }: {
  readonly beacon: NonNullable<CockpitWorkerCard['beacon']>;
  readonly lastActivityAt: string | null | undefined;
  readonly nowMs: number | undefined;
}): ReactElement {
  const age = nowMs === undefined ? null : workerLastActivityText(lastActivityAt, nowMs);
  return (
    <span className="activity-beacon-slot">
      <span className={`activity-beacon activity-beacon-${beacon.kind}`} role="img" aria-label={beacon.label} title={beacon.label} />
      {age !== null && <small className="activity-beacon-age">{age}</small>}
    </span>
  );
}

/** One stable home for every opened Worker surface, keyed by Worker, in roster order. */
function WorkerFocusStage({ cards, presented, onCloseTerminal, renderSurface }: {
  readonly cards: readonly CockpitWorkerCard[];
  readonly presented: string | null;
  readonly onCloseTerminal: () => void;
  readonly renderSurface: (workerId: string, active: boolean) => ReactNode;
}): ReactElement {
  // Workers whose terminal has been opened, in first-open order. The presented Worker joins
  // in this same render so its surface mounts immediately; a Worker that left the roster drops out.
  const [opened, setOpened] = useState<readonly string[]>([]);
  const kept = opened.filter((workerId) => cards.some((card) => card.workerId === workerId));
  const mountedIds = presented !== null && !kept.includes(presented) ? [...kept, presented] : kept;
  if (mountedIds.length !== opened.length || mountedIds.some((workerId, index) => workerId !== opened[index])) {
    setOpened(mountedIds);
  }

  return (
    <div className="worker-focus-stage">
      {cards.filter((card) => mountedIds.includes(card.workerId)).map((card) => {
        const active = card.workerId === presented;
        return (
          <WorkerTerminalWorkspace key={card.workerId} card={card} open={active} onClose={onCloseTerminal}>
            {renderSurface(card.workerId, active)}
          </WorkerTerminalWorkspace>
        );
      })}
    </div>
  );
}
